import React, { createContext, useContext, useEffect, useState } from 'react';
import useAchievementTriggers from '../hooks/useAchievementTriggers';
import axios from '../utils/axiosConfig';
import { useAuth } from './AuthContext';
import { GlobalContext } from './GlobalContext';

export const ApplicationTrackerContext = createContext();

export const ApplicationTrackerProvider = ({ children }) => {
  // Application Tracker context state and effects
  const { onboardingInfo, questPlan, fetchQuest, showToastMessage } = useContext(GlobalContext);
  const { user, accessToken } = useAuth();

  // Initialize all state variables first
  const [refreshKey, setRefreshKey] = useState(0); // increment when you want to refresh applications
  const [currentApps, setCurrentApps] = useState([]);
  const [addApplicationModalOpen, setAddApplicationModalOpen] = useState(false);
  const [scrapeEmailsModalOpen, setScrapeEmailsModalOpen] = useState(false);
  const [appDetailsOpen, setAppDetailsOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [progress, setProgress] = useState(0);
  const [scrapeInterval, setScrapeInterval] = useState(0);
  const [nextIndexToParse, setNextIndexToParse] = useState(0);
  const [test, setTest] = useState(0);
  const [taskId, setTaskId] = useState(null);
  const [scrapingStatus, setScrapingStatus] = useState(null);
  const [loadScraping, setLoadScraping] = useState(false);
  const [isResumingScrape, setIsResumingScrape] = useState(false);
  const [autoScraperInfoModalOpen, setAutoScraperInfoModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [emailScrapingAllowed, setEmailScrapingAllowed] = useState(() => {
    const stored = localStorage.getItem('emailScrapingEnabled');
    return stored ? JSON.parse(stored) : null;
  });

  // temporary: hard coded other companies to apply to
  const [otherCompanies, setOtherCompanies] = useState([
    {
      companyName: 'Google',
      position: 'Software Engineer Intern',
      status: 'Request referral',
      url: 'https://www.google.com',
      hidden: false,
    },
    {
      companyName: 'Apple',
      position: 'Software Engineer Intern',
      status: 'Request referral',
      url: 'https://www.apple.com',
      hidden: false,
    },
    {
      companyName: 'Amazon',
      position: 'Software Engineer Intern',
      status: 'Request referral',
      url: 'https://www.amazon.com',
      hidden: false,
    },
    {
      companyName: 'No referral company',
      position: 'Software Engineer Intern',
      status: '',
      url: 'random.com',
      hidden: false,
    },
  ]);

  const checkAchievements = useAchievementTriggers();

  // Now all the useEffects can safely use any state variables
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isFromEmailAuth = localStorage.getItem('emailScrapingAllowed') === 'true';

    // If we just returned from email auth flow, show the scraping modal
    if (isFromEmailAuth && emailScrapingAllowed) {
      setScrapeEmailsModalOpen(true);
      // Clear the flag
      localStorage.removeItem('emailScrapingAllowed');
    }
  }, [emailScrapingAllowed]);

  // Check the database to see if the user allowed email Scraping
  useEffect(() => {
    if (!user || !accessToken) return;

    const fetchEmailScrapingStatus = async () => {
      try {
        const response = await axios.get(`/api/v1/user/${user.sub}`);
        const isAllowed = response.data.user.allow_email_scraping;
        setEmailScrapingAllowed(isAllowed);
        localStorage.setItem('emailScrapingEnabled', JSON.stringify(isAllowed));
        console.log('Email scraping status:', response.data.user);

        // If email scraping is allowed, also fetch the scraping status
        if (isAllowed) {
          try {
            const statusResponse = await axios.get('/api/v1/email-scraping/status');
            setScrapingStatus(statusResponse.data);
            console.log('Scraping automation status:', statusResponse.data);
          } catch (statusError) {
            console.error('Failed to fetch scraping status:', statusError);
          }
        }
      } catch (error) {
        console.error('Failed to fetch email scraping status:', error);
        // Don't set the state if there's an error to avoid false negatives
      }
    };

    fetchEmailScrapingStatus();
  }, [refreshKey, user, accessToken]); // Also run when refreshKey changes

  // Fetch applications from backend
  useEffect(() => {
    if (!user || !accessToken) return;

    console.log('Refreshing and fetching apps');
    const fetchApplications = async () => {
      try {
        const response = await axios.get(`/api/v1/application/user/${user.sub}/detailed`);
        const apps = response.data.applications;

        // If we have existing apps, mark any new ones
        if (currentApps.length > 0) {
          const existingIds = new Set(currentApps.map((app) => app.application_id));
          apps.forEach((app) => {
            if (!existingIds.has(app.application_id)) {
              app.isNew = true;
            }
          });
        }

        setCurrentApps(apps);
        console.log('length of current apps', apps.length);
        // Check various achievement conditions
        if (apps.length >= 3) {
          checkAchievements.checkApplicationCount();
        }
        console.log('Fetched current apps with useEffect:', apps);
      } catch (error) {
        console.error('Failed to fetch applications:', error);
      }
    };

    fetchApplications();
  }, [refreshKey, user, accessToken]);

  // Check for saved scraping task on component mount
  useEffect(() => {
    if (!user || !accessToken) return;

    let cleanupFunction = null;

    const checkForSavedTask = async () => {
      const savedTaskId = localStorage.getItem('activeScrapingTaskId');
      const scrapingStartTime = localStorage.getItem('scrapingStartTime');

      if (savedTaskId && scrapingStartTime) {
        // Check if task is not too old (more than 1 hour)
        const startTime = new Date(scrapingStartTime);
        const now = new Date();
        const hoursSinceStart = (now - startTime) / (1000 * 60 * 60);

        if (hoursSinceStart > 1) {
          console.log('Saved task is too old, clearing');
          localStorage.removeItem('activeScrapingTaskId');
          localStorage.removeItem('scrapingStartTime');
          return;
        }

        // Check if initial scrape is already complete
        if (scrapingStatus?.is_initial_scrape_complete) {
          console.log('Initial scrape already complete, clearing saved task');
          localStorage.removeItem('activeScrapingTaskId');
          localStorage.removeItem('scrapingStartTime');
          return;
        }

        console.log(`Resuming progress tracking for task ${savedTaskId}`);
        setTaskId(savedTaskId);
        setLoadScraping(true);
        setIsResumingScrape(true);
        setScrapeEmailsModalOpen(true); // Show modal to display progress

        // Resume polling for this task
        cleanupFunction = pollTaskProgress(savedTaskId, true);
      }
    };

    checkForSavedTask();

    // Return cleanup function
    return () => {
      if (typeof cleanupFunction === 'function') {
        cleanupFunction();
      }
    };
  }, [user, accessToken, scrapingStatus?.is_initial_scrape_complete]); // Only depend on completion status, not whole object

  // Removed automatic hourly scraping - now handled by backend cron job

  // Helper function to handle task progress polling
  const pollTaskProgress = (taskId, isResumed = false) => {
    let intervalId = null;

    const checkProgress = async () => {
      try {
        const data = await getProgress(taskId);
        if (data.progress >= 1) {
          clearInterval(intervalId);

          // Backend now processes emails directly into applications
          // No need to call parseArrayEmails anymore
          console.log(
            `✅ Email scraping complete. Found ${data.results?.length || 0} application-related emails.`
          );
          console.log('Applications have been created/updated in the backend.');

          // Clear saved task from localStorage
          localStorage.removeItem('activeScrapingTaskId');
          localStorage.removeItem('scrapingStartTime');

          // Refresh scraping status to reflect the completed initial scrape
          await getScrapingStatus();

          setScrapeEmailsModalOpen(false);
          setLoadScraping(false);
          setIsResumingScrape(false);
          // Refresh the table to show new applications
          setRefreshKey((prevKey) => prevKey + 1);
        }
      } catch (error) {
        // Check if it's a 404 (task doesn't exist)
        if (error.response?.status === 404) {
          console.log('Task no longer exists, clearing saved task');
          localStorage.removeItem('activeScrapingTaskId');
          localStorage.removeItem('scrapingStartTime');
          clearInterval(intervalId);
          setLoadScraping(false);
          setIsResumingScrape(false);
          // Refresh status to get current state
          await getScrapingStatus();
        } else {
          console.error('Error checking progress:', error);
          clearInterval(intervalId);
          setLoadScraping(false);
          setIsResumingScrape(false);
        }
      }
    };

    // Poll for updates every 5 seconds
    intervalId = setInterval(checkProgress, 5000);
    // Also check immediately
    checkProgress();

    // Return cleanup function (not async)
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  };

  // Entry point for email scraping
  async function scrapeBackground() {
    console.log('Scraping background');
    checkAchievements.checkEmailConnection();
    if (!emailScrapingAllowed) {
      console.error('Email scraping not allowed. Please grant access first.');
      return;
    }

    if (startDate === null || endDate === null) {
      return;
    }
    setNextIndexToParse(0);
    setLoadScraping(true);
    setProgress(0.00001);

    try {
      const taskId = await startScrapingEmails(); // Get the taskId from startScrapingEmails
      console.log('task id is', taskId);

      if (!taskId) {
        console.error('No task ID received');
        setLoadScraping(false);
        return;
      }

      // Save task info to localStorage for recovery
      localStorage.setItem('activeScrapingTaskId', taskId);
      localStorage.setItem('scrapingStartTime', new Date().toISOString());

      // Start polling for progress
      return pollTaskProgress(taskId, false);
    } catch (error) {
      console.error('Error starting email scraping:', error);
      setLoadScraping(false);
    }
  }

  async function startScrapingEmails() {
    if (!accessToken) {
      console.error('No access token available for email scraping');
      return;
    }

    setProgress(0);
    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Create dates at midnight and end of day in user's local timezone
    // startDate and endDate come from the date picker
    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(endDate);
    // Set to the very end of the day (23:59:59.999)
    endOfDay.setHours(23, 59, 59, 999);

    // Log for debugging
    console.log('User timezone:', userTimezone);
    console.log('Start date (local):', startOfDay.toString());
    console.log('End date (local):', endOfDay.toString());
    console.log('Start date (ISO/UTC):', startOfDay.toISOString());
    console.log('End date (ISO/UTC):', endOfDay.toISOString());

    const scrapeInfo = {
      start_date: startOfDay.toISOString(),
      end_date: endOfDay.toISOString(),
      timezone: userTimezone,
      // JWT is sent via Authorization header automatically by axios
    };

    try {
      const response = await axios.post(`/api/v1/start-task`, scrapeInfo);
      setTaskId(response.data.task_id);
      return response.data.task_id;
    } catch (error) {
      console.error('Error starting email scraping:', error);
      throw error;
    }
  }

  async function getProgress(taskId) {
    console.log(taskId);
    try {
      const response = await axios.get(`/api/v1/progress/${taskId}`);
      setProgress(response.data.progress);
      return response.data;
    } catch (error) {
      console.error('Error getting progress:', error);
      throw error;
    }
  }

  async function getScrapingStatus() {
    if (!accessToken || !user) {
      return;
    }

    try {
      // axios interceptor automatically adds Authorization header
      const response = await axios.get('/api/v1/email-scraping/status');
      setScrapingStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting scraping status:', error);
      setScrapingStatus(null);
    }
  }

  const deleteApplication = async (applicationID, companyName, positionName) => {
    try {
      const response = await axios.delete(`/api/v1/application/${applicationID}`);
      console.log(response.data.message);
    } catch (error) {
      console.error('Error deleting application:', error);
    }
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const updateApplication = async (
    applicationID,
    oldCompany,
    oldPosition,
    oldStatus,
    newCompany,
    newPosition,
    newStatus,
    roleid
  ) => {
    try {
      // Ensure we have valid values for required fields
      if (!roleid) {
        console.error('No role_id provided for update');
        return;
      }
      if (!newCompany) {
        console.error('No company name provided for update');
        return;
      }

      // First, get the current application data to preserve existing company information
      let currentCompanyData = null;
      try {
        // Find the current application in the state to get company info
        const currentApp = currentApps.find((app) => app.application_id === applicationID);
        if (currentApp) {
          currentCompanyData = {
            company_name: currentApp.company_name,
            company_id: currentApp.company_id,
            logo_url: currentApp.logo_url,
            linkedin_id: currentApp.linkedin_id,
          };
        }
      } catch (error) {
        console.log('Could not fetch current application data');
      }

      // Determine the company data to use
      let companyData = null;
      if (newCompany === oldCompany && currentCompanyData) {
        // Company name hasn't changed, preserve existing data
        companyData = currentCompanyData;
      } else {
        // Company name has changed, try to get existing company info for the new name
        let existingCompanyLogoUrl = null;
        try {
          const companyResponse = await axios.get(
            `/api/v1/company/by-name/${encodeURIComponent(newCompany)}`
          );
          existingCompanyLogoUrl = companyResponse.data.company?.logo_url;
        } catch (error) {
          console.log(
            'Could not fetch existing company data for new company name, will use null for logo_url'
          );
        }

        companyData = {
          company_name: newCompany,
          logo_url: existingCompanyLogoUrl,
        };
      }

      const response = await axios.put(`/api/v1/application/edit`, {
        application_id: applicationID,
        application: {
          user_id: parseInt(user.sub),
        },
        company: companyData,
        role: {
          name: newPosition,
          company_id: companyData?.company_id || null,
          description: '',
          skills: [],
          location: '',
          link: '',
          categories: [],
          post_date: new Date().toISOString().split('T')[0],
          is_public: false,
          is_active: true,
        },
      });

      console.log('Application updated:', response.data);

      // Update the status if it has changed using the existing updateApplicationStatus function
      if (newStatus && newStatus !== oldStatus) {
        updateApplicationStatus(applicationID, newCompany, newPosition, newStatus);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setRefreshKey((prev) => prev + 1);
  };

  const updateApplicationStatus = async (applicationID, companyName, positionName, status) => {
    console.log('In the function - STATUS IS ', status);

    // Get the current application to check previous status
    const currentApp = currentApps.find((app) => app.application_id === applicationID);
    const previousStatus = currentApp?.status;

    // Prepare the application status data
    const applicationStatusData = {
      application_status: {
        application_id: applicationID,
        status: status,
        status_date: new Date().toISOString(),
      },
    };

    try {
      // Make the API call first
      const response = await axios.post(`/api/v1/application/status/create`, applicationStatusData);
      console.log('Status update successful:', response.data);

      // Update quest if status changed from initial to submitted state
      const initialStates = ['Application Desired', 'Application Started'];
      const submittedStates = [
        'Application Submitted',
        'Live Interviewing',
        'Offer Received',
        'Rejected',
        'Online Assessment',
      ];

      if (questPlan && initialStates.includes(previousStatus) && submittedStates.includes(status)) {
        try {
          // Get user timezone for calendar date context
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const questUpdateResponse = await axios.post(
            `/api/v1/quest/${questPlan.quest_id}/add_application_by_date`,
            {
              company_name: companyName,
              application_date:
                currentApp?.application?.last_updated ||
                currentApp?.last_updated ||
                new Date().toISOString(),
            },
            {
              headers: {
                'X-User-Timezone': userTimezone,
              },
            }
          );

          showToastMessage(`Your application to ${companyName} was added to your quest!`);
          fetchQuest();
        } catch (questError) {
          console.warn('Failed to update quest, but status was updated successfully:', questError);
        }
      }

      // Only update the state after successful API call
      let updatedApps = [...currentApps];
      const index = updatedApps.findIndex((app) => app.application_id === applicationID);
      if (index !== -1) {
        updatedApps[index].status = status;
        setCurrentApps(updatedApps);
      }

      // Trigger a refresh to get fresh data from backend
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error updating application status:', error);
      // You might want to show an error message to the user here
    }
  };

  // DEPRECATED: Backend now handles email parsing directly via EmailApplicationService
  // This function is kept for reference but is no longer used
  const parseArrayEmails = async (emails) => {
    console.log('DEPRECATED: parseArrayEmails should not be called - backend handles this now');
    return; // Exit early since backend handles this

    // Original implementation below (no longer executed):
    console.log('ParseArrayEmails');

    let updatedApps = [...currentApps]; // Make a local copy of currentApps
    console.log('updatedApps', updatedApps);
    try {
      console.log(nextIndexToParse, emails.length);
      if (nextIndexToParse === emails.length) {
        return; // Don't parse if nextIndexToParse is equal to the length of the array of emails
      }

      const processEmails = async () => {
        for (let i = nextIndexToParse; i < emails.length; i++) {
          updatedApps = [...currentApps];
          let email = JSON.parse(emails[i]);
          console.log('Full email object structure:', JSON.stringify(email, null, 2));
          console.log('Available email fields:', Object.keys(email));

          // Check if company name exists and its format
          if (email.company) {
            console.log('Found company field:', email.company);
          }
          if (email.companyName) {
            console.log('Found companyName field:', email.companyName);
          }
          if (email.company_name) {
            console.log('Found company_name field:', email.company_name);
          }

          // Determine which field to use for company name
          const companyName = email.company_name || email.companyName || email.company;
          if (!companyName) {
            console.error('No company name found in email data');
            continue;
          }

          if (email.status === 'Not found') {
            continue;
          }
          // Find the index of the element with matching companyName in updatedApps
          const index = updatedApps.findIndex((app) => app.companyName === companyName);

          if (index !== -1) {
            console.log('saw this before');

            // first, make sure we haven't scraped this email before by looking in the history
            if (updatedApps[index].history.some((element) => element.date === email.date)) {
              console.log('already scraped this email');
              continue;
            }
            // If the companyName is already in updatedApps, update the status
            updatedApps[index].status = email.status;
            updatedApps[index].date = email.date;
            console.log('Application ID is', updatedApps[index].applicationID);
            console.log(updatedApps[index]);

            await axios.post(`/api/v1/application/status/create`, {
              application_status: {
                application_id: updatedApps[index].applicationID,
                status: email.status,
                status_date: new Date(email.date),
              },
            });
            console.log('Updated status');

            updatedApps[index].history.push(email);
          } else {
            // Otherwise, add a new element to updatedApps
            email.history = [email];
            // Completely new, add it to the database
            console.log('ADDING THE FOLLOWING APPLICATION');
            console.log(email);

            console.log('Raw email data:', email);

            // Ensure we have a valid status
            const validStatuses = [
              'Application Desired',
              'Application Started',
              'Application Submitted',
              'Live Interviewing',
              'Offer Received',
              'Rejected',
              'Online Assessment',
            ];
            const status = validStatuses.includes(email.status)
              ? email.status
              : 'Application Submitted';

            // Ensure we have a valid date
            const date = email.date ? new Date(email.date) : new Date();
            const formattedDate = date.toISOString();

            // Check if company exists first
            let existingCompany = null;
            let existingCompanyId = null;
            try {
              const companyResponse = await axios.get(
                `/api/v1/company/by-name/${encodeURIComponent(companyName)}`
              );
              if (companyResponse.data.company) {
                existingCompany = companyResponse.data.company;
                existingCompanyId = existingCompany.company_id;
                console.log(`Found existing company: ${companyName} (ID: ${existingCompanyId})`);
              }
            } catch (error) {
              // Company doesn't exist, will create new
              console.log(`Company ${companyName} not found, will create new`);
            }

            // After getting/creating company, check for existing role
            let existingRoleId = null;
            if (existingCompanyId && email.position) {
              try {
                // Search for roles with the exact position name
                // Backend now correctly applies search_term even with include_unpublic
                const roleResponse = await axios.get('/api/v1/role', {
                  params: {
                    search_term: email.position,
                    page: 1,
                    page_size: 50,
                    include_unpublic: true, // Include user's private roles
                    include_inactive: false,
                  },
                });

                console.log(
                  `Found ${roleResponse.data.roles.length} roles matching search "${email.position}"`
                );

                // Find exact match for this company and role name
                // Search may return partial matches, so we need exact match
                const matchingRole = roleResponse.data.roles.find((role) => {
                  // Backend returns role.company_id, not role.company.company_id
                  const companyMatch = role.company_id === existingCompanyId;
                  const nameMatch = role.name === email.position; // Exact name match

                  if (companyMatch) {
                    console.log(
                      `  Checking role at company ${existingCompanyId}: "${role.name}" === "${email.position}" ? ${nameMatch}`
                    );
                  }

                  return companyMatch && nameMatch;
                });

                if (matchingRole) {
                  existingRoleId = matchingRole.role_id;
                  console.log(
                    `✓ Found existing role: "${email.position}" at ${companyName} (ID: ${existingRoleId})`
                  );
                } else {
                  console.log(
                    `✗ No exact matching role found for "${email.position}" at company ${existingCompanyId}`
                  );
                  if (roleResponse.data.roles.length > 0) {
                    console.log(
                      `  Found ${roleResponse.data.roles.length} partial matches but none at company ${existingCompanyId}`
                    );
                  }
                }
              } catch (error) {
                console.log(`Role search failed, will create new: ${error.message}`);
              }
            }

            // Build application data based on whether company and role exist
            let applicationData;

            if (existingRoleId) {
              // Role exists, use it directly
              applicationData = {
                application: {}, // ApplicationInputRequest is empty - user_id auto-populated from JWT
                create_new_role: false,
                existing_role_id: existingRoleId,
              };
            } else {
              // Need to create new role (and possibly new company)
              applicationData = {
                application: {}, // ApplicationInputRequest is empty - user_id auto-populated from JWT
                create_new_role: true,
                new_role: {
                  role: {
                    name: email.position,
                    company_id: existingCompanyId || null, // Use existing ID if available
                    description: '',
                    skills: [],
                    location: '',
                    link: '',
                    categories: [],
                    post_date: formattedDate.split('T')[0],
                    is_active: true,
                    programming_languages: [],
                    frameworks_and_tools: [],
                    job_focus: 'Other',
                    visa_status: 'Not Specified',
                    requires_security_clearance: false,
                    housing_status: 'Not Specified',
                  },
                  create_new_company: !existingCompany, // Only create if doesn't exist
                },
              };

              // Add company data based on whether it exists
              if (existingCompany) {
                applicationData.new_role.existing_company_id = existingCompanyId;
              } else {
                applicationData.new_role.new_company = {
                  company: {
                    company_name: companyName,
                    logo_url: null,
                  },
                };
              }
            }
            console.log('Constructed applicationData:', JSON.stringify(applicationData, null, 2));
            console.log('Company data specifically:', applicationData.company);

            try {
              const response = await axios.post(`/api/v1/application/create`, applicationData);

              if (response.data.application_id) {
                console.log('Application created:', response.data);
                const newApp = {
                  ...email,
                  applicationID: response.data.application_id,
                  status: status,
                  date: formattedDate,
                };
                updatedApps = [...updatedApps, newApp];

                // Add the status
                await axios.post(`/api/v1/application/status/create`, {
                  application_status: {
                    application_id: response.data.application_id,
                    status: status,
                    status_date: formattedDate,
                  },
                });
              }
            } catch (error) {
              console.error('Error sending application data:', error);
            }
          }
          // Update nextIndexToParse to the current index + 1
          setNextIndexToParse(i + 1);
        }
      };

      await processEmails();
      setCurrentApps(updatedApps);
    } catch (error) {
      console.log('Error parsing message data:', error);
    }
  };

  const deleteRecommendedApps = (companyName) => {
    // for the application that matches companyname, change the hidden field to True
    let updatedCompanies = [...otherCompanies]; // Make a local copy of otherCompanies
    const index = updatedCompanies.findIndex((app) => app.companyName === companyName); // Find the index of the element with matching companyName
    if (index !== -1) {
      updatedCompanies[index].hidden = true;
    }
    setOtherCompanies(updatedCompanies); // Update the state of otherCompanies
  };

  const updateRecommendedApps = (companyName, status) => {
    let updatedCompanies = [...otherCompanies]; // Make a local copy of otherCompanies
    const index = updatedCompanies.findIndex((app) => app.companyName === companyName); // Find the index of the element with matching companyName
    if (index !== -1) {
      // if the person is requesting a referral, call the route to actually request one
      if (
        updatedCompanies[index].status === 'Request referral' &&
        status === 'Referral requested'
      ) {
        // TODO ADD ROUTE to request referral
      }
      updatedCompanies[index].status = status;
    }
    setOtherCompanies(updatedCompanies); // Update the state of otherCompanies
  };

  const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  function enableEmailScraping() {
    if (emailScrapingAllowed) {
      console.log('Email scraping already allowed');
      return;
    }

    // Set flag to indicate we're requesting email access
    localStorage.setItem('emailScrapingAllowed', 'true');
    localStorage.setItem('returnToPath', window.location.pathname);

    const clientId = CLIENT_ID;
    const redirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
    const scope = encodeURIComponent(
      'openid https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/gmail.readonly'
    );
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=consent&include_granted_scopes=true`;

    window.location.href = authUrl;
  }

  // Function to remove email scraping access
  const removeEmailScrapingAccess = async () => {
    if (!user?.sub) {
      console.error('No user ID available');
      return;
    }

    try {
      await axios.put(`/api/v1/user/allow-email-scraping/${user.sub}`, {
        allow_email_scraping: false,
      });

      setEmailScrapingAllowed(false);
      // We don't need to clear tokens or redirect to Google OAuth
      // Just update the UI state to reflect that email scraping is disabled
      console.log('Email scraping access has been removed successfully');
    } catch (error) {
      console.error('Error removing email scraping access:', error);
    }
  };

  const sharedData = {
    currentApps,
    setCurrentApps,
    selectedApp,
    setSelectedApp,
    addApplicationModalOpen,
    setAddApplicationModalOpen,
    scrapeEmailsModalOpen,
    setScrapeEmailsModalOpen,
    progress,
    setProgress,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    scrapeInterval,
    setScrapeInterval,
    nextIndexToParse,
    setNextIndexToParse,
    taskId,
    setTaskId,
    scrapingStatus,
    setScrapingStatus,
    getScrapingStatus,
    loadScraping,
    setLoadScraping,
    isResumingScrape,
    setIsResumingScrape,
    otherCompanies,
    setOtherCompanies,
    deleteApplication,
    updateApplication,
    updateApplicationStatus,
    deleteRecommendedApps,
    updateRecommendedApps,
    setRefreshKey,
    scrapeBackground,
    emailScrapingAllowed,
    setEmailScrapingAllowed,
    enableEmailScraping,
    removeEmailScrapingAccess,
    autoScraperInfoModalOpen,
    setAutoScraperInfoModalOpen,
    appDetailsOpen,
    setAppDetailsOpen,
  };

  return (
    <ApplicationTrackerContext.Provider value={sharedData}>
      {children}
    </ApplicationTrackerContext.Provider>
  );
};
